import { html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { buttonStyles } from '../styles/button-styles.js';
import '../session/session-list.js';

/**
 * Player join panel for the landing page.
 * Contains session list and manual join code input.
 *
 * @fires join-session - Fired when player attempts to join with { joinCode }
 */
@customElement('player-join')
export class PlayerJoin extends BaseElement {
    static styles = [
        BaseElement.styles,
        buttonStyles,
        css`
            :host {
                display: flex;
                flex-direction: column;
                align-items: center;
                flex: 1;
                border-radius: 1rem;
                padding: 1.5rem;
            }

            h2 {
                font-size: 1.5rem;
                margin: 0 0 0.5rem;
                text-align: center;
            }

            .panel-subtitle {
                font-size: 0.875rem;
                color: var(--text-secondary);
                text-align: center;
                margin-bottom: 1.5rem;
            }

            .active-sessions-container {
                width: 100%;
                max-width: 300px;
                margin-bottom: 1.5rem;
            }

            .session-list-label {
                font-size: 0.875rem;
                color: var(--text-secondary);
                margin-bottom: 0.5rem;
                text-align: center;
            }

            .join-code-input-container {
                margin-bottom: 1.5rem;
            }

            .join-code-input {
                width: 150px;
                padding: 1rem;
                font-size: 2rem;
                font-weight: 700;
                text-align: center;
                letter-spacing: 0.3em;
                text-transform: uppercase;
                border: 2px solid var(--border);
                border-radius: 0.5rem;
                background-color: var(--background);
                color: var(--text-primary);
                font-family: 'Courier New', Courier, monospace;
            }

            .join-code-input:focus {
                outline: none;
                border-color: var(--primary-color);
                box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.2);
            }

            .join-code-input::placeholder {
                color: var(--text-tertiary);
                opacity: 0.5;
            }

            .error-message {
                color: var(--error-color, #ef4444);
                font-size: 0.875rem;
                text-align: center;
                margin: 1rem 0;
                padding: 0.75rem;
                background-color: rgba(239, 68, 68, 0.1);
                border: 1px solid var(--error-color, #ef4444);
                border-radius: 0.375rem;
            }

            .btn {
                width: 100%;
                max-width: 300px;
                display: block;
            }
        `,
    ];

    @property({ attribute: false })
    sessions: { joinCode: string }[] = [];

    @property({ type: String })
    error = '';

    @property({ type: Boolean })
    loading = false;

    @query('#join-code-input')
    private _joinCodeInput!: HTMLInputElement;

    private _onJoin(): void {
        const joinCode = this._joinCodeInput?.value.trim().toUpperCase();
        if (!joinCode || joinCode.length !== 3) return;

        this.dispatchEvent(
            new CustomEvent('join-session', {
                bubbles: true,
                composed: true,
                detail: { joinCode },
            })
        );
    }

    private _onInputKeydown(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            this._onJoin();
        }
    }

    private _onInput(e: Event): void {
        const input = e.target as HTMLInputElement;
        input.value = input.value.toUpperCase();
        // Clear error on typing
        if (this.error) {
            this.error = '';
        }
    }

    private _onSessionJoin(e: CustomEvent): void {
        // Re-fire session-join from <session-list> as join-session
        e.stopPropagation();
        this.dispatchEvent(
            new CustomEvent('join-session', {
                bubbles: true,
                composed: true,
                detail: { joinCode: e.detail.joinCode },
            })
        );
    }

    /** Clear input and focus it (called by parent on join error) */
    clearInput(): void {
        if (this._joinCodeInput) {
            this._joinCodeInput.value = '';
            this._joinCodeInput.focus();
        }
    }

    render() {
        return html`
            <h2>Join a Session</h2>

            <div class="active-sessions-container">
                <p class="session-list-label">Active Sessions:</p>
                <session-list
                    id="active-sessions-list"
                    .sessions=${this.sessions}
                    @session-join=${this._onSessionJoin}
                ></session-list>
            </div>

            <p class="panel-subtitle">Or enter the join code manually</p>

            <div class="join-code-input-container">
                <input
                    type="text"
                    id="join-code-input"
                    class="join-code-input"
                    maxlength="3"
                    placeholder="ABC"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="characters"
                    spellcheck="false"
                    @input=${this._onInput}
                    @keydown=${this._onInputKeydown}
                >
            </div>

            ${this.error
                ? html`<p class="error-message">${this.error}</p>`
                : nothing}

            <button
                class="btn btn-primary"
                id="join-btn"
                @click=${this._onJoin}
                ?disabled=${this.loading}
            >
                ${this.loading ? 'Joining...' : 'Join Session'}
            </button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'player-join': PlayerJoin;
    }
}
